import React, { useState, useEffect } from 'react';
import { GetQuestions, SubmitQuiz, GetSettings, TrackMetric, CheckDuplicateParticipant } from '../../../utilities/BL/OverlayQuizz';
import { ValidateSqlInjection as BLValidateSqlInjection } from './../../../utilities/BL/Security'

const TERMS_TEXT = `TÉRMINOS Y CONDICIONES DE LA ENCUESTA
1. Organizador de la encuesta: Dorados de Sinaloa es el único responsable organizador de esta encuesta y de la entrega física del premio (jersey).
2. Deslinde de responsabilidad técnica: Five Digital Minds e Interaktia actúan únicamente como los proveedores tecnológicos de la plataforma del juego y queda eximido de cualquier responsabilidad derivada de la entrega de premios o controversias de la encuesta.
3. Participantes: Encuesta válida únicamente para aficionados presentes en el estadio que sean mayores de edad.
4. Registro: Cada participante puede registrarse una sola vez. Intentos múltiples serán anulados.
5. Elección del ganador: Los ganadores se elegirán con base en la puntuación más alta de aciertos y, en caso de empate, por el menor tiempo de respuesta registrado por el servidor.
6. Decisiones: Las decisiones del comité organizador son inapelables y definitivas.`;

const PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO
Sus datos personales (Nombre Completo, Correo Electrónico y Teléfono) serán recabados con la única finalidad de registrar su participación en la encuesta, poder contactarle y validar su identidad en caso de resultar ganador, así como para estadísticas internas de participación de los organizadores.

Al aceptar este aviso, autoriza expresamente que sus datos de contacto sean compartidos y transferidos entre los patrocinadores, el Dorados de Sinaloa y la empresa operadora Five Digital Minds, así como sus asociados comerciales y aliados estratégicos con fines de marketing, estadísticas y entrega de premios. No se realizarán transferencias a otros terceros sin su consentimiento. Para dudas o aclaraciones contacte a soporte.`;

const SOURCE_MAPPING = {
    obs: 'overlay_screen',
    seat: 'seats',
    bath: 'restrooms'
};

function shuffleArray(array) {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

export function OverlayQuizzFan() {
    const [phase, setPhase] = useState('register'); // register, quiz, submitting, finished, error, ended, already_participated
    const [questions, setQuestions] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    
    // Form fields
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    
    // Validation Errors
    const [validationErrors, setValidationErrors] = useState({});
    
    // Modals
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // States for progress flow
    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const [selectedOptionsList, setSelectedOptionsList] = useState([]); // Array of selected option IDs
    const [apiErrorMessage, setApiErrorMessage] = useState('');
    const [formStartTracked, setFormStartTracked] = useState(false);

    const getSessionId = () => {
        let sessId = sessionStorage.getItem('overlayquizz_session_id');
        if (!sessId) {
            sessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('overlayquizz_session_id', sessId);
        }
        return sessId;
    };

    const trackEvent = async (eventName) => {
        const sessId = getSessionId();
        const urlParams = new URLSearchParams(window.location.search);
        const rawSrc = urlParams.get('s') || urlParams.get('utm_medium') || urlParams.get('src') || 'direct';
        const srcVal = SOURCE_MAPPING[rawSrc] || rawSrc;
        try {
            await TrackMetric(eventName, sessId, srcVal);
        } catch (e) {
            console.error('Local tracking error:', e);
        }
        try {
            if (window.gtag) {
                window.gtag('event', `quizzb9_${eventName}`, {
                    session_id: sessId,
                    event_category: 'quizzb9_trivia',
                    event_label: eventName,
                    traffic_source: srcVal
                });
            }
        } catch (e) {
            console.error('GA4 tracking error:', e);
        }
    };

    const handleInputFocus = () => {
        if (!formStartTracked) {
            setFormStartTracked(true);
            trackEvent('form_start');
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            const savedProgressStr = sessionStorage.getItem('overlayquizz_progress');
            let hasProgress = false;
            let parsedProgress = null;

            if (savedProgressStr) {
                try {
                    parsedProgress = JSON.parse(savedProgressStr);
                    if (parsedProgress && parsedProgress.sessionId) {
                        sessionStorage.setItem('overlayquizz_session_id', parsedProgress.sessionId);
                        hasProgress = true;
                    }
                } catch (e) {
                    console.error('Error parsing progress:', e);
                }
            }

            if (!hasProgress) {
                const existingSessId = sessionStorage.getItem('overlayquizz_session_id');
                if (!existingSessId) {
                    sessionStorage.removeItem('overlayquizz_page_view_tracked');
                    sessionStorage.removeItem('overlayquizz_qr_scan_tracked');
                    const newSessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
                    sessionStorage.setItem('overlayquizz_session_id', newSessId);
                }
            }

            if (!sessionStorage.getItem('overlayquizz_page_view_tracked')) {
                sessionStorage.setItem('overlayquizz_page_view_tracked', 'true');
                trackEvent('page_view');
            }

            const urlParams = new URLSearchParams(window.location.search);
            const hasQrParam = urlParams.get('s') || urlParams.get('src') === 'qr';
            if (hasQrParam && !sessionStorage.getItem('overlayquizz_qr_scan_tracked')) {
                sessionStorage.setItem('overlayquizz_qr_scan_tracked', 'true');
                trackEvent('qr_scan');
            }

            const settingsRes = await GetSettings();
            let isActive = true;
            if (settingsRes.success && settingsRes.data) {
                isActive = settingsRes.data.isTriviaActive;
                
                const savedProgress = sessionStorage.getItem('overlayquizz_progress');
                if (!isActive && !savedProgress) {
                    setPhase('ended');
                    return;
                }
            }

            const res = await GetQuestions();
            if (res.success && res.data) {
                const savedProgress = sessionStorage.getItem('overlayquizz_progress');
                if (savedProgress) {
                    try {
                        const parsed = JSON.parse(savedProgress);
                        if (parsed.email && parsed.phone) {
                            setName(parsed.name || '');
                            setEmail(parsed.email || '');
                            setPhone(parsed.phone || '');
                            setAcceptedTerms(true);
                            setCurrentIdx(parsed.currentIndex || 0);
                            setSelectedOptionsList(parsed.answers || []);
                            
                            if (parsed.questionOrder) {
                                const orderedQuestions = parsed.questionOrder.map(qId => 
                                    res.data.find(q => q.Id === qId || q.id === qId)
                                ).filter(Boolean);
                                setQuestions(orderedQuestions);
                                setPhase('quiz');
                                return;
                            }
                        }
                    } catch (err) {
                        sessionStorage.removeItem('overlayquizz_progress');
                    }
                }

                // Randomize options for a new session only if keepOptionsOrder is false
                const loadedQuestions = [...res.data];
                loadedQuestions.forEach(q => {
                    const keepOrder = q.keepOptionsOrder || q.KeepOptionsOrder;
                    if (!keepOrder) {
                        q.options = shuffleArray(q.options);
                    }
                });
                setQuestions(loadedQuestions);
            } else {
                setPhase('error');
                setApiErrorMessage(res.message || 'No se pudieron cargar las preguntas.');
            }
        };
        loadInitialData();
    }, []);

    const handleStartQuiz = async (e) => {
        e.preventDefault();
        setValidationErrors({});

        const errors = {};
        if (!name.trim()) {
            errors.name = 'El nombre completo es requerido.';
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email.trim()) {
            errors.email = 'El correo electrónico es requerido.';
        } else if (!emailRegex.test(email)) {
            errors.email = 'El correo electrónico no tiene un formato válido.';
        }

        const phoneRegex = /^\+?[0-9]{10,15}$/;
        if (!phone.trim()) {
            errors.phone = 'El teléfono / WhatsApp es requerido.';
        } else if (!phoneRegex.test(phone)) {
            errors.phone = 'El teléfono debe ser numérico y contener entre 10 y 15 dígitos.';
        }

        if (!acceptedTerms) {
            errors.acceptedTerms = 'Debes aceptar los términos y el aviso de privacidad para participar.';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Validación de Inyección SQL
        const isNameThreat = await BLValidateSqlInjection(name);
        const isEmailThreat = await BLValidateSqlInjection(email);
        const isPhoneThreat = await BLValidateSqlInjection(phone);

        if (isNameThreat || isEmailThreat || isPhoneThreat) {
            setValidationErrors({ name: 'Caracteres no permitidos en el formulario.' });
            return;
        }

        if (questions.length === 0) {
            setValidationErrors({ name: 'Las preguntas están cargando, por favor espera un momento.' });
            return;
        }

        setPhase('submitting');
        const dupRes = await CheckDuplicateParticipant(email, phone);
        if (dupRes.success && dupRes.exists) {
            setPhase('already_participated');
            sessionStorage.removeItem('overlayquizz_progress');
            sessionStorage.removeItem('overlayquizz_session_id');
            return;
        } else if (!dupRes.success) {
            setPhase('register');
            setValidationErrors({ name: dupRes.message || 'Error al validar tus datos de registro.' });
            return;
        }

        const qOrder = questions.map(q => q.id || q.Id);
        const currentSessionId = getSessionId();
        sessionStorage.setItem('overlayquizz_progress', JSON.stringify({
            name,
            email,
            phone,
            currentIndex: 0,
            answers: [],
            questionOrder: qOrder,
            sessionId: currentSessionId
        }));

        trackEvent('form_complete');
        trackEvent('quiz_start');

        setPhase('quiz');
    };

    const handleSelectOption = (optionId) => {
        setSelectedOptionId(optionId);
    };

    const handleNextQuestion = async () => {
        if (selectedOptionId === null) return;

        const updatedAnswers = [...selectedOptionsList, selectedOptionId];
        setSelectedOptionsList(updatedAnswers);
        setSelectedOptionId(null);

        const qOrder = questions.map(q => q.id || q.Id);

        const currentSessionId = getSessionId();
        sessionStorage.setItem('overlayquizz_progress', JSON.stringify({
            name,
            email,
            phone,
            currentIndex: currentIdx + 1,
            answers: updatedAnswers,
            questionOrder: qOrder,
            sessionId: currentSessionId
        }));

        if (currentIdx < questions.length - 1) {
            setCurrentIdx(currentIdx + 1);
        } else {
            setPhase('submitting');
            const res = await SubmitQuiz(name, email, phone, updatedAnswers, acceptedTerms);
            if (res.success) {
                setPhase('finished');
                trackEvent('quiz_complete');
                sessionStorage.removeItem('overlayquizz_progress');
                sessionStorage.removeItem('overlayquizz_session_id');
            } else if (res.code === 'ALREADY_PARTICIPATED') {
                setPhase('already_participated');
                sessionStorage.removeItem('overlayquizz_progress');
                sessionStorage.removeItem('overlayquizz_session_id');
            } else {
                setPhase('error');
                setApiErrorMessage(res.message || 'Error al procesar el envío de la trivia.');
            }
        }
    };

    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 11000 }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(false)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(false)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    const currentQuestion = questions[currentIdx];

    return (
        <main className="container OverlayQuizzFan my-4 d-flex flex-column align-items-center justify-content-between min-vh-100 px-3">
            {showTermsModal && renderLegalModal('Términos y Condiciones', TERMS_TEXT, setShowTermsModal)}
            {showPrivacyModal && renderLegalModal('Aviso de Privacidad', PRIVACY_TEXT, setShowPrivacyModal)}

            <header className="w-100 text-center mt-3">
                <img 
                    src="/img/page/adsl-five-bicolor.png" 
                    className="img-fluid d-block mx-auto mb-3" 
                    style={{ maxHeight: '75px', objectFit: 'contain' }}
                    alt="ADSL / Five Digital Minds" 
                />
            </header>

            <section className="w-100 flex-grow-1 d-flex flex-column my-4" style={{ maxWidth: '480px' }}>
                <div className="card border-0 p-4 bg-white">
                    
                    {phase === 'register' && (
                        <form onSubmit={handleStartQuiz}>
                            <h2 className="text-center fw-bold mb-4 text-dark fs-3">Registro a la encuesta <span className='d-block p-2'><img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' style={{ width: '220px' }} alt="Logo Patrocinador" /></span></h2>

                            <div className="mb-3">
                                <label className="form-label">Nombre completo</label>
                                <input 
                                    type="text" 
                                    className={`form-control py-2 rounded-3 ${validationErrors.name ? 'is-invalid border-danger' : 'border-secondary-subtle'}`}
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                    onFocus={handleInputFocus}
                                />
                                {validationErrors.name && (
                                    <div className="invalid-feedback small">{validationErrors.name}</div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Correo electrónico</label>
                                <input 
                                    type="text" 
                                    className={`form-control py-2 rounded-3 ${validationErrors.email ? 'is-invalid border-danger' : 'border-secondary-subtle'}`}
                                    value={email} 
                                    onChange={(e) => setEmail(e.target.value)} 
                                    onFocus={handleInputFocus}
                                />
                                {validationErrors.email && (
                                    <div className="invalid-feedback small">{validationErrors.email}</div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Teléfono o WhatsApp</label>
                                <input 
                                    type="text" 
                                    className={`form-control py-2 rounded-3 ${validationErrors.phone ? 'is-invalid border-danger' : 'border-secondary-subtle'}`}
                                    value={phone} 
                                    onChange={(e) => setPhone(e.target.value)} 
                                    onFocus={handleInputFocus}
                                />
                                <label className="form-check-label small text-secondary" htmlFor="acceptTermsCheck" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                    Ej. 4441234567. Incluye código de país (si aplica).
                                </label>
                                {validationErrors.phone && (
                                    <div className="invalid-feedback small">{validationErrors.phone}</div>
                                )}
                            </div>

                            <div className="form-check mb-4 text-start">
                                <input 
                                    className={`form-check-input ${validationErrors.acceptedTerms ? 'is-invalid' : ''}`}
                                    type="checkbox" 
                                    id="acceptTermsCheck" 
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                />
                                <label className="form-check-label small text-secondary" htmlFor="acceptTermsCheck" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                    He leído y acepto los <button type="button" className="btn btn-link p-0 text-danger small border-0 align-baseline" style={{ fontSize: '0.75rem' }} onClick={() => setShowTermsModal(true)}>Términos y Condiciones</button> y el <button type="button" className="btn btn-link p-0 text-danger small border-0 align-baseline" style={{ fontSize: '0.75rem' }} onClick={() => setShowPrivacyModal(true)}>Aviso de Privacidad</button> de la encuesta.
                                </label>
                            </div>

                            <button type="submit" className="Btn__Adsl--Red" style={{ maxWidth: '100%' }}>
                                Iniciar encuesta
                            </button>
                            {validationErrors.acceptedTerms && (
                                <div className="invalid-feedback small d-block">{validationErrors.acceptedTerms}</div>
                            )}
                        </form>
                    )}

                    {phase === 'quiz' && currentQuestion && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="badge bg-danger rounded-pill px-3 py-1.5 small mx-auto">
                                    Pregunta {currentIdx + 1} de {questions.length}
                                </span>
                            </div>
                            
                            {currentQuestion.showLogo || currentQuestion.ShowLogo ? (
                                <div className="text-center mb-4">
                                    <img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' className='mx-auto p-1' style={{ width: '220px', display: 'block' }} alt="Logo" />
                                    <h3 className="fw-bold text-dark fs-4 mt-2 text-uppercase">
                                        {currentQuestion.instruction}
                                    </h3>
                                </div>
                            ) : (
                                <h3 className="fw-bold mb-4 text-center text-dark fs-4 text-uppercase">
                                    {currentQuestion.instruction}
                                </h3>
                            )}

                            <div className="d-flex flex-column gap-3 mb-4">
                                {currentQuestion.options.map((option) => {
                                    const isSelected = selectedOptionId === option.id;
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => handleSelectOption(option.id)}
                                            className={`btn w-100 py-3 rounded-3 text-start px-4 transition-all text-uppercase ${
                                                isSelected 
                                                ? 'btn-danger text-white border-danger shadow' 
                                                : 'btn-outline-danger border-danger-subtle text-dark bg-transparent'
                                            }`}
                                        >
                                            {option.text}
                                        </button>
                                    );
                                })}
                            </div>

                            <button 
                                type="button" 
                                onClick={handleNextQuestion}
                                disabled={selectedOptionId === null}
                                className="Btn__Adsl--Red transition-all"
                                style={{ maxWidth: '100%' }}
                            >
                                {currentIdx < questions.length - 1 ? 'Siguiente' : 'Finalizar encuesta'}
                            </button>
                        </div>
                    )}

                    {phase === 'submitting' && (
                        <div className="text-center py-5">
                            <div className="spinner-border text-danger mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Procesando...</span>
                            </div>
                            <h4 className="fw-bold text-dark">Procesando</h4>
                            <p className="text-muted small">Por favor, espera un momento.</p>
                        </div>
                    )}

                    {phase === 'finished' && (
                        <div className="text-center py-4">
                            <div className="mb-4">
                                <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3 fs-3">¡Muchas gracias por participar!</h3>
                            <p className="text-muted mb-4 px-2">¡Atent@! porque al finalizar el partido, anunciaremos a la persona ganadora del jersey y nos pondremos en contacto con ella.</p>

                            {/* <img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' className='p-1 mx-auto' style={{ width: '220px', display: 'block', marginTop: '1em' }} alt="Logo" />
                            <p className="text-muted mb-1 px-2 mt-4">
                                ¿Quieres más información de casas y departamentos?
                            </p>
                            <a href='https://wa.link/8azzg2' target='_blank' rel="noreferrer" className='btn btn-success' onClick={() => trackEvent('whatsapp_click')}>Quiero más información</a> */}
                        </div>
                    )}

                    {phase === 'already_participated' && (
                        <div className="text-center py-4">
                            <div className="mb-4">
                                <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3 fs-3">¡Gracias por participar!</h3>
                            <p className="text-muted mb-4 px-2">Tus respuestas ya fueron recibidas anteriormente.</p>
                            <p className="text-muted mb-4 px-2">¡Atent@! porque al finalizar el partido, anunciaremos a la persona ganadora del jersey y nos pondremos en contacto con ella.</p>

                            {/* <img src='/img/overlays/rubaquiz/ruba-logotipo-negro.png' className='p-1 mx-auto' style={{ width: '220px', display: 'block', marginTop: '1em' }} alt="Logo" />
                            <p className="text-muted mb-1 px-2 mt-4">
                                ¿Quieres más información de casas y departamentos?
                            </p>
                            <a href='https://wa.link/8azzg2' target='_blank' rel="noreferrer" className='btn btn-success' onClick={() => trackEvent('whatsapp_click')}>Quiero más información</a> */}
                        </div>
                    )}

                    {phase === 'ended' && (
                        <div className="text-center py-5">
                            <div className="mb-4">
                                <i className="bi bi-calendar-x-fill text-danger" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3">Encuesta cerrada</h3>
                            <p className="text-muted mb-4 px-3">
                                Lo sentimos, la encuesta de Barrio 9 ha finalizado por ahora. ¡Muchas gracias por tu interés y participación!
                            </p>
                        </div>
                    )}

                    {phase === 'error' && (
                        <div className="text-center py-4">
                            <div className="mb-4">
                                <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '4.5rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-dark mb-3">Ha habido un detalle</h3>
                            <p className="text-muted mb-4 px-2">
                                {apiErrorMessage || 'No ha sido posible procesar tu participación en este momento.'}
                            </p>
                            <button 
                                type="button" 
                                className="btn btn-danger px-5 py-2 rounded-3 fw-bold shadow-sm"
                                onClick={() => {
                                    setPhase('register');
                                    sessionStorage.removeItem('overlayquizz_progress');
                                }}
                            >
                                Reintentar
                            </button>
                        </div>
                    )}
                </div>
            </section>

            <p className='text-center FdmFooter FooterCredits'>
                <a href='https://interaktia.com/?utm_source=quizz_b9_web&utm_medium=footer&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='text-dark'>Hecho con ♥ por Interaktia | 2026</a>
            </p>
        </main>
    );
}
export default OverlayQuizzFan;
